import { Component, EventEmitter, Inject, LOCALE_ID, OnInit, Output, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { forkJoin } from 'rxjs';
import { PredictiveAddressSearchComponent } from 'src/app/components/predictive-address-search/predictive-address-search.component';
import { PageContentModel } from 'src/app/models/page-content.model';
import { PSUser } from 'src/app/models/ps-user.model';
import { TPWorker } from 'src/app/models/worker.model';
import { PageContentService } from 'src/app/services/page-content/page-content.service';
import { TeamMemberService } from 'src/app/services/team-member/team-member.service';
import { UserService } from 'src/app/services/uppy/user.service';
import { WorkerService } from 'src/app/services/worker-service/worker.service';
import { TrainingProgramApplicationUtils } from 'src/app/utils/training-program-application.util';
import { emailValidatorPattern, phoneValidatorPattern } from 'src/app/utils/validators.util';
declare function openNewTab(url: string): void

@Component({
    selector: 'app-worker-profile-setup',
    templateUrl: './worker-profile-setup.component.html',
    styleUrls: ['./worker-profile-setup.component.scss'],
    providers: [{ provide: 'TeamMemberService', useClass: TeamMemberService }],
    standalone: false
})
export class WorkerProfileSetupComponent implements OnInit {
  learner: TPWorker = new TPWorker();
  currentUser: PSUser
  workerForm: FormGroup;
  currentLang: string = 'en';
  sideBarContents: PageContentModel[];
  created: boolean = false;
  window: any = (<any>window);
  disableNameInputs: boolean = false

  @Output() enrollmentConfirmationChanged = new EventEmitter<boolean>();
  @ViewChild(PredictiveAddressSearchComponent, { static: false }) childC: PredictiveAddressSearchComponent;
  constructor(
    @Inject(LOCALE_ID) public locale: string,
    public translateService: TranslateService, public formBuilder: FormBuilder,
    public router: Router,
    public route: ActivatedRoute,
    @Inject('PageContentService') public pageContentService: PageContentService,
    @Inject('WorkerService') public workerService: WorkerService,
    public userService: UserService,
    public snackBar: MatSnackBar) {
    this.created = this.router.url.toLocaleLowerCase().endsWith('/created');
  }

  ngOnInit(): void {
    this.currentLang = this.translateService.currentLang;
    forkJoin({
      currentWorker: this.workerService.GetCurrentWorker(),
      currentUser: this.userService.GetCurrentUser()
    }).subscribe(x => {
      this.learner = x.currentWorker;
      this.currentUser = x.currentUser
      if (this.learner == null || this.learner.Id == null || this.learner.Id == TrainingProgramApplicationUtils.EmptyGuid) {
        this.workerService.GetPersonDetailsByEmail(this.currentUser.EmailAddress!).subscribe(p => {
          if (p && p.PersonId) {
            this.disableNameInputs = true
            this.workerService.GetCurrentWorkerByPersonId(p.PersonId!).subscribe(w => {
              if (w) {
                this.learner = w
                if (TrainingProgramApplicationUtils.isNullOrEmpty(this.learner.FirstName)) this.learner.FirstName = this.currentUser.FirstName
                if (TrainingProgramApplicationUtils.isNullOrEmpty(this.learner.LastName)) this.learner.LastName = this.currentUser.LastName
                this.learner.EmailAddress = this.currentUser.EmailAddress!
                if (TrainingProgramApplicationUtils.isNullOrEmpty(this.learner.FirstName)) this.learner.FirstName = p.FirstName
                if (TrainingProgramApplicationUtils.isNullOrEmpty(this.learner.LastName)) this.learner.LastName = p.LastName
              }
              this.buildForm();
            },
            (error) => { // probably unauthorized
              this.router.navigate(['/worker/error']);
            })
          } else { 
            this.buildForm(); 
          }
        })
      } else {
        this.buildForm();
      }
    },
    (error) => {
      this.router.navigate(['/worker/error']);
    }
  );
    this.pageContentService.GetPageContentByPageName("WorkerProfileSidebar").subscribe(x => this.sideBarContents = x);
  }

  ngAfterViewInit(): void {
    this.userService.hideWorkerMenu = true;
  }

  buildForm() {
    this.workerForm = new FormGroup(
      {
        firstName: new FormControl(this.learner.FirstName, [Validators.required]),
        lastName: new FormControl(this.learner.LastName, [Validators.required]),
        emailAddress: new FormControl(this.currentUser.EmailAddress, [Validators.pattern(emailValidatorPattern), Validators.required]),
        altEmailAddress: new FormControl(this.learner.SecondaryEmailAddress, [Validators.pattern(emailValidatorPattern)]),
        phone: new FormControl(this.learner.PhoneNumber, [Validators.pattern(phoneValidatorPattern), Validators.required]),
        altPhone: new FormControl(this.learner.SecondaryPhoneNumber, [Validators.pattern(phoneValidatorPattern)]),
        AddressForm: this.formBuilder.group({
          address: new FormControl(this.learner.Address ?? '', [Validators.maxLength(500), Validators.required]),
          aptSuitUnit: new FormControl(this.learner.AptSuiteUnit, [Validators.maxLength(500)]),
          city: new FormControl(this.learner.City, [Validators.required, Validators.maxLength(100)]),
          province: new FormControl(this.learner.Province, [Validators.required]),
          postalCode: new FormControl(this.learner.PostalCode?.toUpperCase(), [Validators.required, Validators.maxLength(7)]),
        }),
        poBox: new FormControl(this.learner.PoBox),
        birthYear: new FormControl(this.learner.BirthYear, [Validators.required]),
        learnerId: new FormControl(this.learner.WorkerDisplayId),
      }
    )

  }

  saveWorker() {
    if (this.workerForm.valid) {
      if(!this.disableNameInputs) this.learner.FirstName = this.workerForm.controls['firstName'].value;
      if(!this.disableNameInputs) this.learner.LastName = this.workerForm.controls['lastName'].value;
      this.learner.PhoneNumber = this.workerForm.controls['phone'].value;
      this.learner.SecondaryPhoneNumber = this.workerForm.controls['altPhone'].value;
      if(!this.disableNameInputs) this.learner.EmailAddress = this.workerForm.controls['emailAddress'].value;
      this.learner.SecondaryEmailAddress = this.workerForm.controls['altEmailAddress'].value;
      this.learner.Address = this.workerForm.get('AddressForm.address')!.value;
      this.learner.AptSuiteUnit = this.workerForm.get('AddressForm.aptSuitUnit')!.value;
      this.learner.City = this.workerForm.get('AddressForm.city')!.value;
      this.learner.Province = this.workerForm.get('AddressForm.province')!.value;
      this.learner.PostalCode = this.workerForm.get('AddressForm.postalCode')!.value.toUpperCase();
      this.learner.PoBox = this.workerForm.get('poBox')!.value;
      this.learner.BirthYear = this.workerForm.controls['birthYear'].value;
      this.learner.WorkerDisplayId = this.workerForm.controls['learnerId'].value;
      this.workerService.SetUpWorkerProfile(this.learner).subscribe(res => {
        if(this.learner.Id == TrainingProgramApplicationUtils.EmptyGuid 
          && res !=TrainingProgramApplicationUtils.EmptyGuid )
          {
            this.router.navigate([this.currentLang == 'en' ? '/worker/profile-review' : '/fr/worker/profile-review'] , { queryParams: { DisplayFtuMessage: 'true' }});
          }
          else
          {
            this.router.navigate([this.currentLang == 'en' ? '/worker/certifications' : '/fr/worker/certifications']);
          }
        },
        err=>this.snackBar.open('Profile setup failed , please contact us.','',{duration: 9000, verticalPosition: 'top'})
      );
    }
    else {
      this.workerForm.markAllAsTouched();
    }
  }

  resendVerificationLink() {
    console.log('TBD - resendVerificationLink');
  }

  cancelAccountSetup(){
    let url = this.currentLang == 'en'?'/safety-and-prevention/learners':'/fr/safety-and-prevention/learners';
    this.router.navigate([url])
  }

  public ngOnDestroy() {
    this.userService.hideWorkerMenu = false;
  }
}
